import { useCallback, useEffect, useState } from 'react';

/** The `localStorage` key of the operator's choice. `index.html` has an inline script that reads it
 *  before the first paint, and `theme.spec.ts` checks that the two agree. */
export const THEME_KEY = 'ops-console-theme';

export type Theme = 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function stored(): Theme | null {
  try {
    const value = window.localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function system(): Theme {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * The console's theme. With no stored choice it follows the system or browser setting and its
 * changes. `toggle()` sets the other theme, stores it, and stops following the system for the rest
 * of the page's life, also when the write fails. The theme is the `data-theme` attribute on
 * `<html>`, which `theme.css` reads. Clearing the site data returns to the system setting.
 */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => stored() ?? system());
  const [following, setFollowing] = useState(() => stored() === null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    if (!following) return;
    const query = window.matchMedia?.(DARK_QUERY);
    if (!query) return;
    const onChange = () => setTheme(system());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [following]);

  const toggle = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setFollowing(false);
    try {
      window.localStorage.setItem(THEME_KEY, next);
    } catch {
      // Storage is blocked: the choice holds for this page only.
    }
  }, [theme]);

  return { theme, toggle };
}
